/**
 * @file Fetch one encrypted media file from Cloud Storage with exponential
 * backoff retry (max 3 attempts). Mirrors export_architecture_v2 §7.2.
 *
 * The Firebase Storage SDK doesn't expose getBytes for a path string, so we
 * resolve the storage path through `ref()` then call `getBytes()`.
 */

import {
  type FirebaseStorage,
  type StorageReference,
  getBytes,
  ref as storageRef,
} from "firebase/storage";

export class MediaFetchError extends Error {
  public readonly storagePath: string;
  public readonly innerCause: unknown;
  constructor(storagePath: string, innerCause: unknown) {
    super(`failed to fetch media after retries: ${storagePath}`);
    this.name = "MediaFetchError";
    this.storagePath = storagePath;
    this.innerCause = innerCause;
  }
}

export interface FetchMediaOptions {
  /** Max attempts (default 3). */
  maxAttempts?: number;
  /** Sleep used between retries; injectable for tests. */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

export async function fetchEncryptedMedia(
  storage: FirebaseStorage,
  storagePath: string,
  options: FetchMediaOptions = {},
): Promise<Uint8Array> {
  const maxAttempts = options.maxAttempts ?? 3;
  const sleep = options.sleep ?? defaultSleep;
  const ref: StorageReference = storageRef(storage, storagePath);
  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const buf = await getBytes(ref);
      return new Uint8Array(buf);
    } catch (e) {
      lastErr = e;
      if (attempt >= maxAttempts) break;
      await sleep(2 ** attempt * 1000);
    }
  }
  throw new MediaFetchError(storagePath, lastErr);
}
